/**
 * Calls the backend to mint a remote adaptive-screener link for a patient.
 * @agentic connections
 */

import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";

/** Successful response from POST /api/v1/surveys/generate-screener-link. @agentic connections */
export type ScreenerLinkApiResponse = {
  url: string;
  token: string;
};

/**
 * Requests a DOB-gated remote adaptive-screener link for the given patient.
 * The server resolves first-check-in vs follow-up at open; the caller only
 * provides the patient. Link validity defaults to the enforced 14-day maximum.
 * @agentic connections
 */
export const requestScreenerLink = async (params: {
  accessToken: string;
  patientId: string;
  signal?: AbortSignal;
}): Promise<ScreenerLinkApiResponse> => {
  const apiUrl = getApiEndpoint("/api/v1/surveys/generate-screener-link");

  const response = await fetch(apiUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Medplum-Authorization": `Bearer ${params.accessToken}`,
      ...getApiAuthHeaders(),
    },
    body: JSON.stringify({ patientId: params.patientId }),
    signal: params.signal,
  });

  const raw = await response.text();
  let body: Record<string, unknown> = {};
  try {
    body = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
  } catch {
    body = { raw };
  }

  if (!response.ok) {
    const message =
      (typeof body.error === "string" ? body.error : null) ||
      `Could not create screener link (${response.status})`;
    throw new Error(message);
  }

  const url = body.url;
  const token = body.token;
  if (typeof url !== "string" || typeof token !== "string") {
    throw new Error("Invalid response from screener link service");
  }

  return { url, token };
};
